/**
 * Jump to the very top of the page, reliably on phones:
 * - resets window, <html> and <body> (the site CSS lets <body> scroll on its own on phones)
 * - repeats for ~half a second, so iOS momentum scrolling or late layout changes
 *   (content replacing a loading spinner) cannot leave the page in the middle
 */
const RESET_DELAYS_MS = [0, 50, 150, 300, 600];

const resetScrollPosition = () => {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

// Signs that the visitor is scrolling by hand — then the repeated resets must stop,
// otherwise the page jumps back to the top under their finger (looks like a blink).
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"] as const;

let cancelPendingResets: (() => void) | null = null;

export const scrollToTopReliably = () => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
  cancelPendingResets?.();

  const timers = RESET_DELAYS_MS.map((delay) => window.setTimeout(resetScrollPosition, delay));
  const stopResets = () => {
    timers.forEach((timer) => window.clearTimeout(timer));
    USER_SCROLL_EVENTS.forEach((eventName) => window.removeEventListener(eventName, stopResets));
    window.clearTimeout(cleanupTimer);
    cancelPendingResets = null;
  };
  // Any scroll by hand cancels the resets still waiting
  USER_SCROLL_EVENTS.forEach((eventName) => window.addEventListener(eventName, stopResets, { passive: true }));
  const cleanupTimer = window.setTimeout(stopResets, RESET_DELAYS_MS[RESET_DELAYS_MS.length - 1] + 50);
  cancelPendingResets = stopResets;
};
